import {createTheme} from '@mui/material/styles';
import {responsiveFontSizes} from '@mui/material/styles';

// Use the unified theme system
const lightTokens = {
  palette: {
    mode: 'light',
    primary: {
      main: '#0A8FDC',
      light: '#4FC3F7',
      dark: '#0277BD',
      contrastText: '#fff',
    },
    secondary: {
      main: '#F04F47',
      light: '#FF6B6B',
      dark: '#D32F2F',
      contrastText: '#fff',
    },
    error: {
      main: '#F44336',
      light: '#EF5350',
      dark: '#D32F2F',
      contrastText: '#fff',
    },
    warning: {
      main: '#FF9800',
      light: '#FFB74D',
      dark: '#F57C00',
      contrastText: '#fff',
    },
    info: {
      main: '#2196F3',
      light: '#42A5F5',
      dark: '#1976D2',
      contrastText: '#fff',
    },
    success: {
      main: '#11C15B',
      light: '#4CAF50',
      dark: '#2E7D32',
      contrastText: '#fff',
    },
    background: {
      default: '#F4F7FE',
      paper: '#FFFFFF',
    },
    text: {
      primary: 'rgb(17, 24, 39)',
      secondary: 'rgb(107, 114, 128)',
      disabled: 'rgb(149, 156, 169)',
    },
    grey: {
      50: '#fafafa',
      100: '#F5F6FA',
      200: '#edf2f7',
      300: '#E0E0E0',
      400: '#c5c6cb',
      500: '#A8A8A8',
      600: '#666666',
      700: '#4a5568',
      800: '#201e21',
      900: '#1a202c',
      A100: '#d5d5d5',
      A200: '#aaaaaa',
      A400: '#303030',
      A700: '#616161',
    },
    divider: 'rgba(224, 224, 224, 1)',
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
  typography: {
    fontFamily: ['Poppins', 'sans-serif'].join(','),
    fontSize: 14,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
      letterSpacing: '-0.025em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      lineHeight: 1.3,
      letterSpacing: '-0.025em',
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 500,
      lineHeight: 1.4,
      letterSpacing: '0',
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: '0',
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 500,
      lineHeight: 1.6,
      letterSpacing: '0',
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.43,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'none',
    },
    caption: {
      fontSize: '0.75rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    overline: {
      fontSize: '0.75rem',
      fontWeight: 500,
      textTransform: 'uppercase',
      letterSpacing: '0.025em',
    },
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.05)',
    '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
    '0px 4px 6px rgba(0, 0, 0, 0.07), 0px 2px 4px rgba(0, 0, 0, 0.06)',
    '0px 10px 15px rgba(0, 0, 0, 0.1), 0px 4px 6px rgba(0, 0, 0, 0.05)',
    '0px 20px 25px rgba(0, 0, 0, 0.1), 0px 10px 10px rgba(0, 0, 0, 0.04)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
    '0px 25px 50px rgba(0, 0, 0, 0.25)',
  ],
  zIndex: {
    mobileStepper: 1000,
    fab: 1050,
    speedDial: 1050,
    appBar: 1100,
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
};

// Dark theme tokens
const darkTokens = {
  ...lightTokens,
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
      light: '#e3f2fd',
      dark: '#42a5f5',
      contrastText: '#000',
    },
    secondary: {
      main: '#f48fb1',
      light: '#fce4ec',
      dark: '#c2185b',
      contrastText: '#000',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
      contrastText: '#fff',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
      contrastText: '#000',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
      contrastText: '#fff',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      contrastText: '#000',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#fff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
    },
  },
};

// Arabic font family
const arabicFontFamily = ['Noto Sans Arabic', 'Arial', 'sans-serif'];

// Component style overrides using unified design system
const createComponentOverrides = (theme) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        textTransform: 'none',
        fontWeight: 500,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: theme.shadows[2],
        },
        '&.MuiButton-contained': {
          boxShadow: theme.shadows[1],
          '&:hover': {
            boxShadow: theme.shadows[3],
          },
        },
      },
      sizeSmall: {
        padding: '8px 16px',
        fontSize: '0.875rem',
      },
      sizeMedium: {
        padding: '12px 24px',
        fontSize: '1rem',
      },
      sizeLarge: {
        padding: '16px 32px',
        fontSize: '1.125rem',
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        boxShadow: theme.shadows[1],
        '&:hover': {
          boxShadow: theme.shadows[3],
        },
      },
    },
  },
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: 12,
        fontWeight: 500,
      },
      standardSuccess: {
        backgroundColor: '#f0fdf4',
        color: '#15803d',
        '& .MuiAlert-icon': {
          color: '#16a34a',
        },
      },
      standardError: {
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
        '& .MuiAlert-icon': {
          color: '#dc2626',
        },
      },
      standardWarning: {
        backgroundColor: '#fffbeb',
        color: '#b45309',
        '& .MuiAlert-icon': {
          color: '#d97706',
        },
      },
      standardInfo: {
        backgroundColor: '#f0f9ff',
        color: '#0369a1',
        '& .MuiAlert-icon': {
          color: '#0284c7',
        },
      },
    },
  },
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 20,
        fontWeight: 500,
      },
      colorPrimary: {
        backgroundColor: '#e0f2fe',
        color: '#0369a1',
      },
      colorSecondary: {
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
      },
      colorSuccess: {
        backgroundColor: '#f0fdf4',
        color: '#15803d',
      },
      colorError: {
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
      },
      colorWarning: {
        backgroundColor: '#fffbeb',
        color: '#b45309',
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 16,
        boxShadow: theme.shadows[6],
      },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        backgroundColor: '#1f2937',
        color: '#ffffff',
        fontSize: '0.875rem',
        borderRadius: 8,
        padding: '8px 16px',
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid #e5e7eb',
      },
      indicator: {
        backgroundColor: '#0A8FDC',
        height: 3,
        borderRadius: '3px 3px 0 0',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '1rem',
        padding: '16px 24px',
        '&.Mui-selected': {
          color: '#0A8FDC',
        },
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        '& .MuiTableHead-root': {
          '& .MuiTableCell-root': {
            fontWeight: 600,
            fontSize: '0.875rem',
            color: '#374151',
            borderBottom: '2px solid #e5e7eb',
          },
        },
        '& .MuiTableBody-root': {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: '#f9fafb',
            },
          },
        },
      },
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        padding: 8,
        borderRadius: 8,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      },
    },
  },
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 12,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0A8FDC',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0A8FDC',
            borderWidth: 2,
          },
        },
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: 12,
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        boxShadow: theme.shadows[4],
        border: '1px solid #e5e7eb',
      },
    },
  },
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid #e5e7eb',
      },
    },
  },
  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: '#f3f4f6',
        '&::after': {
          background:
            'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
        },
      },
    },
  },
  MuiCircularProgress: {
    styleOverrides: {
      root: {
        color: '#0A8FDC',
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        backgroundColor: '#e5e7eb',
        borderRadius: 4,
      },
      bar: {
        backgroundColor: '#0A8FDC',
        borderRadius: 4,
      },
    },
  },
  MuiSnackbar: {
    styleOverrides: {
      root: {
        '& .MuiSnackbarContent-root': {
          borderRadius: 12,
          boxShadow: theme.shadows[3],
        },
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 16,
      },
      elevation1: {
        boxShadow: theme.shadows[1],
      },
      elevation2: {
        boxShadow: theme.shadows[2],
      },
      elevation3: {
        boxShadow: theme.shadows[3],
      },
    },
  },
  MuiLink: {
    styleOverrides: {
      root: {
        fontWeight: 500,
        textDecoration: 'none',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
    },
  },
});

// Create theme with tokens and component overrides
const createAppTheme = (tokens, direction = 'ltr') => {
  const theme = createTheme({
    ...tokens,
    direction,
    typography: {
      ...tokens.typography,
      fontFamily:
        direction === 'rtl'
          ? arabicFontFamily.join(',')
          : tokens.typography.fontFamily,
    },
    components: createComponentOverrides,
  });

  return responsiveFontSizes(theme);
};

// Export theme creation function
export const getAppTheme = (mode = 'light', direction = 'ltr') => {
  const tokens = mode === 'dark' ? darkTokens : lightTokens;
  return createAppTheme(tokens, direction);
};

// Export default light theme
export const defaultTheme = getAppTheme('light', 'ltr');
